import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, TextField, Button, Chip, Tooltip, InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useGetAllocationMerchantsQuery } from '../../../redux/allocationApiSlice';
import { TAXONOMY } from './constants';
import TierBadge from './components/TierBadge';
import StatusPill from './components/StatusPill';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import {
  setActiveMerchant,
  openAllocSourceModal,
  openOtpModal,
} from '../../../redux/d2cAllocationSlice';

function ModeChip({ modeId }) {
  const mode = TAXONOMY.modes[modeId];
  if (!mode) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Tooltip title={mode.desc} arrow placement="top">
      <Chip
        size="small"
        label={`${mode.icon} ${mode.label}`}
        sx={{
          height: 22, fontSize: 10.5, fontWeight: 700,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          color: 'primary.dark',
          border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
        }}
      />
    </Tooltip>
  );
}

export default function D2CAllocationListing() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data: apiData, isLoading, isFetching } = useGetAllocationMerchantsQuery({
    ...(search ? { q: search } : {}),
    page,
    limit: rowsPerPage,
  });

  const merchants = apiData?.merchants || apiData?.data || [];
  const total     = apiData?.total ?? merchants.length;

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(0); };

  const startEdit = (merchant) => {
    dispatch(setActiveMerchant(merchant.id));
    navigate(`/merchant-config/d2c-allocation/${merchant.id}`);
    if (merchant.tier === 'Platinum') {
      dispatch(openOtpModal({
        title: `⭐ Platinum client — OTP required to edit "${merchant.name}"`,
        body: `Allocation edits for platinum clients require approval at the start of the journey. An OTP has been emailed to the registered platinum approver.`,
        purpose: 'edit_platinum',
      }));
    } else {
      dispatch(openAllocSourceModal());
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Merchant',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.3 }}>
            {row.name}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.disabled', fontFamily: 'monospace' }}>
            {row.merchantCode || row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 120,
      minWidth: 180,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => row.tier
        ? <TierBadge tier={row.tier} />
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      minWidth: 180,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => <StatusPill status={row.status || 'Active'} />,
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 130,
      minWidth: 130,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => row.version ? (
        <Stack spacing={0.25} alignItems="center">
          <Chip
            size="small"
            label={row.version}
            sx={{
              height: 20, fontSize: 10.5, fontWeight: 700,
              bgcolor: (t) => alpha(t.palette.info.main, 0.12),
              color: 'info.dark',
              border: (t) => `1px solid ${alpha(t.palette.info.main, 0.28)}`,
              '& .MuiChip-label': { px: 0.9 },
            }}
          />
          {row.effFrom && (
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
              eff. {row.effFrom}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.disabled">Unpublished</Typography>
      ),
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 180,
      minWidth: 200,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => row.mode
        ? <ModeChip modeId={row.mode} />
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'ruleCount',
      headerName: 'Rules',
      width: 90,
      minWidth: 90,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => row.ruleCount != null ? (
        <Chip
          size="small"
          label={row.ruleCount}
          sx={{
            height: 20, fontSize: 11, fontWeight: 700, minWidth: 36,
            bgcolor: (t) => row.ruleCount > 0
              ? alpha(t.palette.success.main, 0.12)
              : alpha(t.palette.grey[500], 0.1),
            color: row.ruleCount > 0 ? 'success.dark' : 'text.secondary',
            border: (t) => row.ruleCount > 0
              ? `1px solid ${alpha(t.palette.success.main, 0.3)}`
              : '1px solid transparent',
            '& .MuiChip-label': { px: 0.9 },
          }}
        />
      ) : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'publishedBy',
      headerName: 'Published by',
      minWidth: 180,
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => row.publishedBy ? (
        <Tooltip title={row.publishedBy} arrow placement="top">
          <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
            {row.publishedBy}
          </Typography>
        </Tooltip>
      ) : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      minWidth: 90,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<EditOutlinedIcon sx={{ fontSize: '13px !important' }} />}
          onClick={() => startEdit(row)}
          sx={{ fontSize: 11, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 1.5, textTransform: 'none' }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
            Merchant allocations
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total > 0 ? `${total} merchant${total !== 1 ? 's' : ''}` : 'Loading…'} · draft / published / active versions
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by name or code…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <AutorenewIcon sx={{
                  fontSize: 15, color: 'text.disabled',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                }} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: 'grey.50' } }}
        />
      </Stack>

      {/* Grid */}
      <ProzoDataGrid
        rows={merchants.map((m) => ({ ...m, id: m.id || m._id }))}
        columns={columns}
        rowCount={total}
        page={page}
        setPage={setPage}
        sizePerPage={rowsPerPage}
        setSizePerPage={(n) => { setRowsPerPage(n); setPage(0); }}
        paginationMode="server"
        loading={isLoading}
        flat={false}
        getRowHeight={() => 'auto'}
      />
    </Box>
  );
}
